import { _getLogger } from '@doxyme/analytics';
import { Specs, attachHandler } from '@doxyme/extensions-common';
import ExtensionCapability from '../../capability';
import type Instance from '../../instance';

const logger = _getLogger('transcription', {
  component: 'extensions',
  lib: 'bridge',
});

type Constraints = Specs.Transcription.Constraints;
type CompletedPayload = Specs.Transcription.CompletedPayload;
type CompletedStatusPayload = Specs.Transcription.CompletedStatusPayload;
type TranscriptionOutput = Specs.Transcription.TranscriptionOutput;
type TranscriptionConfig = Specs.Transcription.Config;
type StartPayload = Specs.Transcription.StartPayload;

const { Transcription } = Specs;

/** Default output configuration when not specified in manifest */
const DEFAULT_OUTPUT: TranscriptionOutput[] = ['transcript'];

export type TranscriptionId = string;
type ManifestId = string;

/**
 * Handler called when starting a transcription.
 * @param output - Array of output types requested ('transcript' and/or 'audio')
 * @returns Promise resolving to the transcription ID
 */
export type StartHandler = (
  output: TranscriptionOutput[],
) => Promise<TranscriptionId>;
export type StopHandler = (transcriptionId: TranscriptionId) => Promise<void>;
export type CancelHandler = (transcriptionId: TranscriptionId) => Promise<void>;

/**
 * Transcription allows extensions to use the transcription service within the host application.
 */
export default class TranscriptionCapability extends ExtensionCapability<Constraints> {
  static readonly id = Transcription.id;

  protected readonly startHandlers: StartHandler[] = [];

  protected readonly stopHandlers: StopHandler[] = [];

  protected readonly cancelHandlers: CancelHandler[] = [];

  /**
   * Active transcriptions: manifest ID → transcription ID.
   * Presence means the manifest has a running transcription.
   * Non-presence means no transcription is active for that manifest.
   */
  #activeTranscriptions: Map<ManifestId, TranscriptionId> = new Map();

  /**
   * Map of transcriptions awaiting completion
   */
  #pendingTranscriptions: Record<string, TranscriptionId[]> = {};

  /**
   * Promises of stop transcription operations.
   * Key is manifest ID.
   * Presence means the transcription is being stopped.
   * Non-presence means not being stopped or already stopped.
   */
  #stopTranscriptionPromises: Map<ManifestId, Promise<void>> = new Map();

  /**
   * Promises of cancel transcription operations.
   * Key is manifest ID.
   * Presence means the transcription is being cancelled.
   * Non-presence means not being cancelled or already cancelled.
   */
  #cancelTranscriptionPromises: Map<ManifestId, Promise<void>> = new Map();

  /**
   * Tracks which output types ('transcript', 'audio', or both) were requested per transcription.
   * Used by completed() to determine when all expected data has arrived.
   */
  #requestedOutputs: Map<TranscriptionId, TranscriptionOutput[]> = new Map();

  /**
   * Accumulates partial completion data across multiple completed() calls.
   * When both transcript and audio are requested, the backend sends two separate
   * realtime messages — one for each output. This map holds the merged data until
   * all requested outputs are satisfied.
   */
  #partialCompletions: Map<TranscriptionId, CompletedPayload> = new Map();

  /**
   * Safety timeout per transcriptionId. If the second completed() call never arrives
   * (e.g. due to a backend failure), the timeout forces notification with partial data
   * rather than hanging indefinitely.
   */
  #completionTimeouts: Map<TranscriptionId, ReturnType<typeof setTimeout>> =
    new Map();

  constructor() {
    super(Transcription);

    this.handlers.push(
      [Transcription.Actions.Start, this.#start.bind(this)],
      [Transcription.Actions.Stop, this.#stop.bind(this)],
      [Transcription.Actions.Cancel, this.#cancel.bind(this)],
      [Transcription.Actions.State, this.#stateTranscription.bind(this)],
    );
  }

  onStart(handler: StartHandler): () => void {
    return attachHandler<StartHandler>(this.startHandlers, handler);
  }

  onStop(handler: StopHandler): () => void {
    return attachHandler<StopHandler>(this.stopHandlers, handler);
  }

  onCancel(handler: CancelHandler): () => void {
    return attachHandler<CancelHandler>(this.cancelHandlers, handler);
  }

  /**
   * Notify transcription completed.
   *
   * When multiple outputs are requested (e.g. both 'transcript' and 'audio'), the backend
   * sends two separate realtime messages — one for each output. This method buffers partial
   * data and only notifies the extension once all requested outputs are present.
   *
   * @returns true if the extension was notified (all outputs satisfied or error forwarded),
   *          false if still buffering and waiting for remaining outputs. The caller should
   *          defer cleanup (e.g. deleting the backend entity) until this returns true,
   *          to avoid a race condition where the entity is deleted before the second
   *          file service callback can send its realtime message.
   */
  completed(payload: CompletedStatusPayload): boolean {
    const { transcriptionId } = payload;

    const manifestId = Object.keys(this.#pendingTranscriptions).find((mId) =>
      this.#pendingTranscriptions[mId]?.includes(transcriptionId),
    );
    if (!manifestId) {
      const errorMessage =
        'Received completed event for unregistered transcriptionId';
      logger.error(errorMessage, {
        transcriptionId,
        pendingTranscriptions: this.#pendingTranscriptions,
        action: 'completed',
      });
      return true;
    }

    if (!payload.isSuccess) {
      this.#pendingTranscriptions[manifestId] =
        this.#pendingTranscriptions[manifestId]?.filter(
          (tId) => tId !== transcriptionId,
        ) ?? [];
      this.#cleanupTracking(transcriptionId);

      const instance = this.getInstanceByManifestId(manifestId);
      instance.notify([Transcription.Events.CompletedStatus, payload]);
      return true;
    }

    const merged = this.#mergeCompletedData(
      this.#partialCompletions.get(transcriptionId),
      payload.data ?? { text: '' },
    );
    this.#partialCompletions.set(transcriptionId, merged);

    const requested =
      this.#requestedOutputs.get(transcriptionId) ?? DEFAULT_OUTPUT;

    if (!this.#isOutputSatisfied(merged, requested)) {
      this.#startCompletionTimeout(transcriptionId);
      return false;
    }

    this.#pendingTranscriptions[manifestId] =
      this.#pendingTranscriptions[manifestId]?.filter(
        (tId) => tId !== transcriptionId,
      ) ?? [];
    this.#cleanupTracking(transcriptionId);

    const instance = this.getInstanceByManifestId(manifestId);
    instance.notify([
      Transcription.Events.CompletedStatus,
      { isSuccess: true, transcriptionId, data: merged },
    ]);
    return true;
  }

  /**
   * "Start" action handler, calls all assigned startHandlers
   * Handlers are awaited and "Started" event is triggered directly here,
   * instead of exposing a separate method.
   */
  async #start(
    payload: StartPayload | undefined,
    instance: Instance<Constraints>,
  ): Promise<void> {
    const manifestId = instance.manifest.id;

    if (this.#activeTranscriptions.has(manifestId)) {
      const errorMessage = 'Transcription already started';
      logger.error(errorMessage, this.#getLoggerProps(instance, 'start'));
      instance.notify([
        Transcription.Events.StartStatus,
        { isSuccess: false, errorMessage },
      ]);
      return;
    }

    // Use output from the action payload if provided, otherwise default to ['transcript'].
    // The manifest config is only used to validate that requested outputs are declared.
    // An empty array is treated as "not provided".
    const configs = instance.configsBy(
      Transcription.id,
    ) as TranscriptionConfig[];
    const config = configs[0];
    const manifestOutput = config?.output ?? DEFAULT_OUTPUT;
    const payloadOutput =
      payload?.output && payload.output.length > 0 ? payload.output : undefined;

    if (
      payloadOutput &&
      !payloadOutput.every((value) => manifestOutput.includes(value))
    ) {
      const errorMessage =
        'Requested output types must be declared in the manifest';
      logger.error(errorMessage, this.#getLoggerProps(instance, 'start'));
      instance.notify([
        Transcription.Events.StartStatus,
        { isSuccess: false, errorMessage },
      ]);
      return;
    }

    const output: TranscriptionOutput[] = payloadOutput ?? DEFAULT_OUTPUT;

    let transcriptionId: TranscriptionId | undefined = undefined;
    try {
      for await (const handler of this.startHandlers) {
        transcriptionId = await handler(output);
      }
    } catch (error) {
      const message = 'Transcription start failed';
      logger.error(message, this.#getLoggerProps(instance, 'start', error));
      instance.notify([
        Transcription.Events.StartStatus,
        {
          isSuccess: false,
          errorMessage: this.#getErrorMessage(error, message),
        },
      ]);
      return;
    }

    // to make it simple - update with a response from the latest handler
    this.#activeTranscriptions.set(manifestId, transcriptionId!);
    this.#requestedOutputs.set(transcriptionId!, output);
    instance.notify([
      Transcription.Events.StartStatus,
      { isSuccess: true, transcriptionId: transcriptionId as string },
    ]);
  }

  /**
   * Public method to stop all active transcriptions (e.g. when call ends)
   */
  async stop() {
    if (this.#activeTranscriptions.size === 0) {
      return;
    }

    const entries = [...this.#activeTranscriptions.entries()];
    await Promise.all(
      entries.map(([manifestId]) => {
        const instance = this.getInstanceByManifestId(manifestId);
        return this.#stopTranscription(instance);
      }),
    );
  }

  /**
   * "Stop" action handler.
   */
  async #stop(payload: never, instance: Instance<Constraints>): Promise<void> {
    if (
      !this.#isManifestIdDefined(
        instance,
        'stop',
        Transcription.Events.StopStatus,
      )
    ) {
      return;
    }

    return this.#stopTranscription(instance);
  }

  /**
   * Calls all assigned stopHandlers
   * Returns existing promise per manifest to avoid multiple api calls
   */
  async #stopTranscription(instance: Instance): Promise<void> {
    const manifestId = instance.manifest.id;
    const existing = this.#stopTranscriptionPromises.get(manifestId);
    if (existing) return existing;

    const promise = this.#validateAndExecuteAction(
      instance,
      'stop',
      Transcription.Events.StopStatus,
      this.stopHandlers,
      () => {
        const transcriptionId = this.#activeTranscriptions.get(manifestId);
        // save to pending to wait for completion, clear from active to allow new transcription
        this.#pendingTranscriptions[manifestId] = [
          ...(this.#pendingTranscriptions[manifestId] ?? []),
          transcriptionId!,
        ];
      },
    ).finally(() => {
      this.#stopTranscriptionPromises.delete(manifestId);
    });

    this.#stopTranscriptionPromises.set(manifestId, promise);
    return promise;
  }

  async #stateTranscription(payload: never, instance: Instance): Promise<void> {
    instance.notify([
      Transcription.Events.StateStatus,
      {
        isSuccess: true,
        state: this.#activeTranscriptions.has(instance.manifest.id)
          ? 'unavailable'
          : 'available',
      },
    ]);
  }

  /**
   * "Cancel" action handler, calls all assigned cancelHandlers
   * Handlers are awaited and "Canceled" event is triggered directly here,
   * instead of exposing a separate method.
   * Returns existing promise to avoid multiple api calls
   */
  async #cancel(
    _payload: never,
    instance: Instance<Constraints>,
  ): Promise<void> {
    if (
      !this.#isManifestIdDefined(
        instance,
        'cancel',
        Transcription.Events.CancelStatus,
      )
    ) {
      return;
    }

    const manifestId = instance.manifest.id;
    const transcriptionId = this.#activeTranscriptions.get(manifestId);
    if (transcriptionId) {
      this.#cleanupTracking(transcriptionId);
    }

    const existing = this.#cancelTranscriptionPromises.get(manifestId);
    if (existing) return existing;

    const promise = this.#validateAndExecuteAction(
      instance,
      'cancel',
      Transcription.Events.CancelStatus,
      this.cancelHandlers,
    ).finally(() => {
      this.#cancelTranscriptionPromises.delete(manifestId);
    });

    this.#cancelTranscriptionPromises.set(manifestId, promise);
    return promise;
  }
  /**
   * Util that holds common logic for stop and cancel actions (which are really similar)
   */
  async #validateAndExecuteAction(
    instance: Instance,
    action: 'stop' | 'cancel',
    event:
      | Specs.Transcription.Events.StopStatus
      | Specs.Transcription.Events.CancelStatus,
    handlers: StopHandler[] | CancelHandler[],
    onHandlersSuccess?: () => void,
  ): Promise<void> {
    const manifestId = instance.manifest.id;
    const transcriptionId = this.#activeTranscriptions.get(manifestId);

    if (!transcriptionId) {
      const errorMessage = `An extension that has not started cannot ${action} transcription`;
      logger.error(errorMessage, this.#getLoggerProps(instance, action));
      instance.notify([event, { isSuccess: false, errorMessage }]);
      return;
    }

    try {
      for await (const handler of handlers) {
        await handler(transcriptionId);
      }
    } catch (error) {
      const errorMessage = `Transcription ${action} failed`;
      logger.error(errorMessage, this.#getLoggerProps(instance, action, error));
      instance.notify([
        event,
        {
          isSuccess: false,
          errorMessage: this.#getErrorMessage(error, errorMessage),
          transcriptionId,
        },
      ]);
      return;
    }

    onHandlersSuccess?.();

    instance.notify([event, { isSuccess: true, transcriptionId }]);

    this.#activeTranscriptions.delete(manifestId);
  }

  /**
   * Merges two CompletedPayload objects, preferring meaningful values.
   * Uses || for text (prefer non-empty string) because the audio-only message
   * sends text: '' as a placeholder. Uses ?? for other fields (prefer first defined value)
   * since they are either present with real data or undefined.
   */
  #mergeCompletedData(
    existing: CompletedPayload | undefined,
    incoming: CompletedPayload,
  ): CompletedPayload {
    if (!existing) return { ...incoming };
    return {
      text: existing.text || incoming.text,
      confidence: existing.confidence ?? incoming.confidence,
      language_code: existing.language_code ?? incoming.language_code,
      language_confidence:
        existing.language_confidence ?? incoming.language_confidence,
      utterances: existing.utterances ?? incoming.utterances,
      audioBlob: existing.audioBlob ?? incoming.audioBlob,
    };
  }

  /**
   * Content-based check instead of counting completed() calls.
   * Inspects the accumulated data for the presence of output-specific fields:
   * - 'transcript' is satisfied when there is meaningful transcript content — i.e.
   *   non-empty text, or confidence, or utterances. This distinguishes real transcript
   *   data from the audio-only placeholder which always sends { text: '', audioBlob }.
   * - 'audio' is satisfied when audioBlob is defined.
   *
   * This makes the bridge resilient to receiving all data in one call or in multiple calls.
   * If the backend is ever changed to send a single combined message, the bridge will
   * see all conditions met immediately and notify without waiting.
   */
  #isOutputSatisfied(
    data: CompletedPayload,
    requested: TranscriptionOutput[],
  ): boolean {
    for (const output of requested) {
      if (output === 'transcript') {
        const hasTranscriptContent =
          (data.text !== undefined && data.text !== '') ||
          data.confidence !== undefined ||
          data.utterances !== undefined;
        if (!hasTranscriptContent) return false;
      }
      if (output === 'audio' && data.audioBlob === undefined) {
        return false;
      }
    }
    return true;
  }

  #cleanupTracking(transcriptionId: TranscriptionId): void {
    this.#requestedOutputs.delete(transcriptionId);
    this.#partialCompletions.delete(transcriptionId);
    const timeout = this.#completionTimeouts.get(transcriptionId);
    if (timeout) clearTimeout(timeout);
    this.#completionTimeouts.delete(transcriptionId);
  }

  /**
   * Starts a safety timeout that will force-notify the extension with whatever partial
   * data has been collected. This prevents the extension from hanging forever if the
   * second backend message never arrives (e.g. due to a backend failure or the entity
   * being deleted before the second file service callback).
   */
  #startCompletionTimeout(transcriptionId: TranscriptionId): void {
    if (this.#completionTimeouts.has(transcriptionId)) return;
    const timeout = setTimeout(
      () => this.#forceComplete(transcriptionId),
      30_000,
    );
    this.#completionTimeouts.set(transcriptionId, timeout);
  }

  #forceComplete(transcriptionId: TranscriptionId): void {
    const manifestId = Object.keys(this.#pendingTranscriptions).find((mId) =>
      this.#pendingTranscriptions[mId]?.includes(transcriptionId),
    );
    if (!manifestId) return;

    const partialData = this.#partialCompletions.get(transcriptionId);

    logger.warn('Completion timeout reached — notifying with partial data', {
      transcriptionId,
      hasTranscript: partialData?.confidence !== undefined,
      hasAudio: partialData?.audioBlob !== undefined,
      action: 'forceComplete',
    });

    this.#pendingTranscriptions[manifestId] =
      this.#pendingTranscriptions[manifestId]?.filter(
        (tId) => tId !== transcriptionId,
      ) ?? [];

    this.#cleanupTracking(transcriptionId);

    const instance = this.getInstanceByManifestId(manifestId);
    instance.notify([
      Transcription.Events.CompletedStatus,
      {
        isSuccess: true,
        transcriptionId,
        data: partialData ?? { text: '' },
      },
    ]);
  }

  #isManifestIdDefined(
    instance: Instance,
    action: 'stop' | 'cancel',
    event:
      | Specs.Transcription.Events.StopStatus
      | Specs.Transcription.Events.CancelStatus,
  ): boolean {
    if (this.#activeTranscriptions.has(instance.manifest.id)) {
      return true;
    }

    const errorMessage =
      "No active transcription for manifest, event won't be triggered";
    logger.error(errorMessage, this.#getLoggerProps(instance, action));
    instance.notify([
      event,
      { isSuccess: false, transcriptionId: undefined, errorMessage },
    ]);

    return false;
  }

  /**
   * Simple util to get an error message from an error
   */
  #getErrorMessage(error: unknown, defaultMessage: string): string {
    return (error as { message?: string })?.message ?? defaultMessage;
  }

  /**
   * Util to get generic logger props
   */
  #getLoggerProps(instance: Instance, action: string, error?: unknown) {
    const manifestId = instance.manifest.id;
    return {
      manifest: { id: manifestId, name: instance.manifest.name },
      activeTranscriptions: Object.fromEntries(this.#activeTranscriptions),
      transcriptionId: this.#activeTranscriptions.get(manifestId),
      action,
      error,
    };
  }
}
